import React, { Component } from 'react';
import { withStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import promisify from '@lba-dev/package.local-globals/promisify';
import { shallowEqual } from 'react-redux';

import api from '../../api';
import ws from '../../ws';

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    margin: 30
  }
};

export default function withAPISubscription(fetch, actions, {
  shouldReload = (props, prevProps) => !shallowEqual(props, prevProps),
  default: defaultData = [],
  customHandling = false,
  name = 'data',
  deverse = false
} = {}) {
  return WrappedComponent => withStyles(class extends Component {
    state = {
      loading: false,
      error: false,
      errorMessage: 'Une erreur est survenue, veuillez rechargez la page',
      data: defaultData
    }

    constructor(props) {
      super(props);
      this.socketActions = this.formatSocketActions(actions, props);
    }

    componentDidMount() {
      this.reload();
      this.toggleSocketActions();
    }

    componentDidUpdate(prevProps) {
      if (shouldReload(this.props, prevProps)) {
        this.reload();
      }
    }

    componentWillUnmount() {
      this.toggleSocketActions(true);
    }

    formatSocketActions = (actions, props) =>
      actions && actions.length
        ? actions.map(({ action, func = f => f() }) => {
          if (typeof action === 'function') {
            action = action(props);
          }
          return {
            action,
            func: (...args) => func(
              (fetcher = fetch) => this.fetchFromAPI(
                fetcher,
                props?.files ? { ...props, files: undefined } : props,
                ...args
              ),
              props,
              ...args
            )
          };
        })
        : []

    toggleSocketActions = remove =>
      this.socketActions.forEach(({ action, func }) => {
        ws[remove ? 'removeEventListener' : 'on'](action, func);
      })

    fetchFromAPI = (fetcher, props, ...args) => {
      if (this.state.loading) {
        return;
      }
      this.promisifiedSetState({
        loading: true,
        error: false
      })
        .then(() => promisify(fetcher(api, props, ...args)))
        .then(data => this.setState({ loading: false, data }))
        .catch((e) => {
          const update = {
            loading: false,
            error: true,
            errorMessage: 'Une erreur est survenue, veuillez rechargez la page',
          };
          if (e && e.response && e.response.statusCode === 404) {
            update.errorMessage = 'Aucun element trouvé';
          }
          this.setState(update);
        });
    }

    reload = (forceUpdate = false) => this.fetchFromAPI(
      fetch,
      this.props,
      forceUpdate
    )

    render() {
      const { loading, error, errorMessage, data } = this.state;
      const { classes, ...props } = this.props;
      const componentProps = {
        ...props,
        ...(deverse ? data : { [name]: data }),
        reload: this.reload
      };

      if (customHandling) {
        componentProps.error = error;
        componentProps.loading = loading;
      }
      return customHandling
        ? <WrappedComponent {...componentProps} />
        : (error && <Typography
          align="center"
          variant="h4"
          children={errorMessage}
        />) || (loading && <div
          className={classes.container}
          children={<CircularProgress size={80} />}
        />) || <WrappedComponent {...componentProps} />;
    }
  }, styles);
}
